import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { adminApi } from '../../adminApi';
import { IconUsers, IconSearch, IconBan, IconShieldCheck, IconStar, IconArrowRight, IconShield, IconGem, IconCheck } from '../../components/Icon';

// Plan → icon + tint. Icon slightly tilted for playful hierarchy (per design ask).
const PLAN_META: Record<string, { Icon: any; label: string; cls: string }> = {
  free:             { Icon: IconShield,      label: 'Free',     cls: 'text-slate-300 bg-slate-500/15 border-slate-500/30' },
  gold_annual:      { Icon: IconStar,        label: 'Gold',     cls: 'text-amber-300 bg-amber-500/15 border-amber-500/35' },
  gold_monthly:     { Icon: IconStar,        label: 'Gold',     cls: 'text-amber-300 bg-amber-500/15 border-amber-500/35' },
  platinum_annual:  { Icon: IconGem,         label: 'Platinum', cls: 'text-cyan-200 bg-cyan-500/15 border-cyan-500/35' },
  platinum_monthly: { Icon: IconGem,         label: 'Platinum', cls: 'text-cyan-200 bg-cyan-500/15 border-cyan-500/35' },
};
function PlanBadge({ plan }: { plan?: string }) {
  const key = (plan || 'free').toLowerCase();
  const meta = PLAN_META[key] || { Icon: IconShield, label: plan || 'Free', cls: 'text-slate-300 bg-slate-500/15 border-slate-500/30' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[11px] font-bold uppercase tracking-wider ${meta.cls}`}>
      <meta.Icon width={12} height={12} style={{ transform: 'rotate(-12deg)' }} />
      {meta.label}
    </span>
  );
}
import { AdminHeader, Reveal } from './ui';

export default function AdminUsers() {
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState(params.get('q')||'');
  const [filter, setFilter] = useState(params.get('filter')||'all');
  const [page, setPage] = useState(Number(params.get('page')||1));

  const load = () => {
    adminApi.users({ page: String(page), limit:'20', q, filter }).then(r=>{
      if (r.status===200) { setRows(r.data?.users||[]); setTotal(r.data?.total||0); }
    });
  };
  useEffect(load, [page, filter]);

  const search = () => { setPage(1); setParams({ q, filter, page:'1' }); load(); };

  return (
    <div>
      <AdminHeader
        icon={IconUsers}
        title="Users"
        subtitle={`${total} accounts · HMAC + MFA · soft-delete to trash`}
        actions={
          <>
            <select value={filter} onChange={e=>{setFilter(e.target.value); setPage(1);}} className="rounded-xl bg-black/30 border border-[color:var(--color-line)] px-3 py-2 text-sm">
              <option value="all">All</option><option value="pro">Pro</option><option value="free">Free</option><option value="banned">Banned</option><option value="tester">Tester</option>
            </select>
            <button onClick={load} className="btn btn-ghost">Refresh</button>
          </>
        }
      />

      <div className="flex gap-4">
        <input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&search()} placeholder="Search username, email, id…" className="flex-1 rounded-xl bg-white/[0.04] border border-[color:var(--color-line)] px-4 py-2.5 text-sm outline-none focus:border-[color:var(--color-brand)]" />
        <button onClick={search} className="btn btn-primary"><IconSearch width={16} height={16}/> Search</button>
      </div>

      <Reveal className="mt-6 glass overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)] border-b border-[color:var(--color-line)]">
              <tr><th className="text-left px-6 py-4">User</th><th className="text-left px-6">Plan</th><th className="text-left px-6">Usage</th><th className="text-left px-6">Created</th><th className="text-right px-6">Action</th></tr>
            </thead>
            <tbody>
              {rows.map(u=>(
                <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-6 py-4"><div className="font-semibold">{u.username} <span className="text-xs text-[color:var(--color-ink-faint)]">#{u.id}</span></div><div className="text-xs text-[color:var(--color-ink-dim)]">{u.email||'—'}</div></td>
                  <td className="px-6 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <PlanBadge plan={u.plan_type} />
                      {u.is_pro && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider text-emerald-300 bg-emerald-500/15 border-emerald-500/30">
                          <IconShieldCheck width={12} height={12} />PRO
                        </span>
                      )}
                      {u.role === 'tester' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider text-fuchsia-300 bg-fuchsia-500/15 border-fuchsia-500/30">
                          <IconStar width={12} height={12} />Tester
                        </span>
                      )}
                      {u.ban_status === 'banned' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider text-red-300 bg-red-500/15 border-red-500/30">
                          <IconBan width={12} height={12} />Banned
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider text-emerald-300 bg-emerald-500/15 border-emerald-500/30">
                          <IconCheck width={12} height={12} />Active
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 text-xs">{u.bytes_used||0} / {u.bytes_total||0} · {u.pro_expiry? String(u.pro_expiry).split(' ')[0] : '—'}</td>
                  <td className="px-6 text-xs text-[color:var(--color-ink-faint)]">{u.created_at? String(u.created_at).slice(0,10): '—'}</td>
                  <td className="px-6 text-right"><Link to={`/admin/users/${u.id}`} className="inline-flex items-center gap-1 text-[color:var(--color-brand-glow)] hover:underline">Manage <IconArrowRight width={12} height={12} /></Link></td>
                </tr>
              ))}
              {rows.length===0 && <tr><td colSpan={5} className="px-6 py-12 text-center text-sm text-[color:var(--color-ink-faint)]">No users match.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-6 py-4 text-xs text-[color:var(--color-ink-faint)]">
          <span>Total {total}</span>
          <div className="flex items-center gap-2">
            <button disabled={page<=1} onClick={()=>setPage(p=>p-1)} className="btn btn-ghost !min-h-0 !py-1.5 !px-3 disabled:opacity-40">Prev</button>
            <span className="px-1 tabular-nums">Page {page}</span>
            <button onClick={()=>setPage(p=>p+1)} className="btn btn-ghost !min-h-0 !py-1.5 !px-3">Next</button>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
